import React, { useState, useMemo } from "react";
import { useQuery, useAction } from "wasp/client/operations";
import { getTextbooks, deleteTextbook } from "wasp/client/operations";
import { useAuth } from "wasp/client/auth";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DashboardLayout } from "../../client/components/Sidebar";
import { useLocationContext } from "../../client/contexts/LocationContext";
import { useRequireSectionVisible } from "../../client/hooks/useRequireSectionVisible";
import {
  EnhancedActionButton,
  EnhancedIconButton,
  enhancedPageLayoutStyles,
  useResponsiveLayout,
} from "../../components/ui/enhanced-page-layout";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../../components/ui/sheet";
import { Button } from "../../components/ui/button";
import { cn } from "../../lib/utils";
import {
  Search,
  Filter,
  Plus,
  Eye,
  Edit,
  Trash2,
  DollarSign,
  BookOpen,
} from "lucide-react";

export default function TextbooksPage() {
  // useRequireSectionVisible('showTextbooks'); // TODO: Add this capability if needed
  const { data: user } = useAuth();
  const { getLocationIdsForQuery, selectedLocation } = useLocationContext();
  const navigate = useNavigate();
  const deleteTextbookFn = useAction(deleteTextbook);

  // State for filters and view mode
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [sortBy, setSortBy] = useState<"newest" | "oldest" | "title" | "price">(
    "newest",
  );

  // Use responsive layout hook
  const {
    viewMode,
    setViewMode,
    isFilterExpanded,
    setIsFilterExpanded,
    isMobile,
    shouldCollapseSidebar,
    hideViewSwitcher,
  } = useResponsiveLayout("cards");

  // Fetch items
  const {
    data: items = [],
    isLoading,
    error,
  } = useQuery(getTextbooks, {
    locationIds: getLocationIdsForQuery(),
    filters: {},
  });

  // Filter and sort items
  const filteredItems = useMemo(() => {
    let filtered = items as any[];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (item: any) =>
          item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.metadata?.author
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          item.metadata?.course
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()),
      );
    }

    // Category filter
    if (selectedCategory !== "all") {
      filtered = filtered.filter(
        (item: any) => item.metadata?.category === selectedCategory,
      );
    }

    // Sort
    switch (sortBy) {
      case "newest":
        filtered = [...filtered].sort(
          (a: any, b: any) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        );
        break;
      case "oldest":
        filtered = [...filtered].sort(
          (a: any, b: any) =>
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
        );
        break;
      case "title":
        filtered = [...filtered].sort((a: any, b: any) =>
          a.title.localeCompare(b.title),
        );
        break;
      case "price":
        filtered = [...filtered].sort(
          (a: any, b: any) =>
            (b.metadata?.price || 0) - (a.metadata?.price || 0),
        );
        break;
    }

    return filtered;
  }, [items, searchTerm, selectedCategory, sortBy]);

  const handleDelete = async (itemId: string) => {
    if (
      window.confirm("Are you sure you want to delete this textbook listing?")
    ) {
      try {
        await deleteTextbookFn({ itemId });
      } catch (error) {
        console.error("Error deleting item:", error);
        alert("Error deleting item. Please try again.");
      }
    }
  };

  const getCategoryColor = (category: string) => {
    return category === "BUY"
      ? "bg-success/10 text-success border border-success/20"
      : category === "BORROW"
        ? "bg-warning/10 text-warning border border-warning/20"
        : "bg-primary/10 text-primary border border-primary/20";
  };

  const getConditionColor = (condition: string) => {
    const colors = {
      NEW: "bg-success/10 text-success border border-success/20",
      LIKE_NEW: "bg-primary/10 text-primary border border-primary/20",
      GOOD: "bg-warning/10 text-warning border border-warning/20",
      FAIR: "bg-secondary/10 text-secondary border border-secondary/20",
      POOR: "bg-destructive/10 text-destructive border border-destructive/20",
    };
    return (
      colors[condition as keyof typeof colors] ||
      "bg-muted text-muted-foreground border border-border"
    );
  };

  return (
    <DashboardLayout
      forceSidebarCollapse={shouldCollapseSidebar}
      showViewSwitcher={!hideViewSwitcher}
      activeView={viewMode}
      onViewChange={setViewMode}
    >
      <div className="w-full h-full flex flex-col">
        {/* Fixed Main Header */}
        <div className="fixed top-[var(--header-height)] left-0 lg:left-[var(--sidebar-width)] right-0 z-20 bg-white border-b border-gray-200 transition-all duration-300">
          <div className="px-4 py-3">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Textbooks</h1>
                <p className="text-sm text-gray-600 mt-1">
                  Browse textbooks in {selectedLocation?.name} (
                  {filteredItems.length})
                </p>
              </div>
              <div className="flex items-center gap-3">
                <EnhancedActionButton
                  onClick={() => setIsFilterExpanded(!isFilterExpanded)}
                  icon={Filter}
                  label=""
                  variant={isFilterExpanded ? "primary" : "secondary"}
                  className="hidden lg:flex"
                />

                {/* Mobile Filter Button */}
                <Sheet open={isFilterExpanded && isMobile} onOpenChange={setIsFilterExpanded}>
                  <EnhancedActionButton
                    onClick={() => setIsFilterExpanded(true)}
                    icon={Filter}
                    label=""
                    variant={isFilterExpanded ? "primary" : "secondary"}
                    className="lg:hidden"
                  />
                  <SheetContent side="right" className="w-[300px] sm:w-[400px]">
                    <SheetHeader>
                      <SheetTitle>Filters</SheetTitle>
                    </SheetHeader>
                    <div className="py-6 space-y-6">
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Search</label>
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <input
                            type="text"
                            placeholder="Search by title, author, course..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-medium">Category</label>
                        <select
                          value={selectedCategory}
                          onChange={(e) => setSelectedCategory(e.target.value)}
                          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="all">All Types</option>
                          <option value="BUY">Want to Buy</option>
                          <option value="SELL">Want to Sell</option>
                          <option value="BORROW">Want to Borrow</option>
                        </select>
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-medium">Sort By</label>
                        <select
                          value={sortBy}
                          onChange={(e) => setSortBy(e.target.value as any)}
                          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="newest">Newest First</option>
                          <option value="oldest">Oldest First</option>
                          <option value="title">Title A-Z</option>
                          <option value="price">Price (High-Low)</option>
                        </select>
                      </div>

                      <Button 
                        className="w-full" 
                        onClick={() => setIsFilterExpanded(false)}
                      >
                        Apply Filters
                      </Button>
                    </div>
                  </SheetContent>
                </Sheet>
                {user?.isAdmin && (
                  <EnhancedActionButton
                    onClick={() => {
                      if (selectedLocation?.locationCode) {
                        navigate(
                          `/locations/${selectedLocation.locationCode}/textbooks/add`,
                        );
                      } else {
                        navigate("/textbooks/add");
                      }
                    }}
                    icon={Plus}
                    label="Add Textbook"
                    hideLabelOnMobile
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Fixed Filters (Desktop) */}
        <div
          className={cn(
            "fixed top-[calc(var(--header-height)+5rem)] left-0 lg:left-[var(--sidebar-width)] right-0 z-10 bg-white border-b border-gray-200 transition-all duration-300 ease-in-out hidden lg:block",
            isFilterExpanded ? "translate-y-0 opacity-100" : "-translate-y-full opacity-0 pointer-events-none"
          )}
        >
          <div className="px-4 py-3">
            <div className="flex items-center gap-4">
              {/* Search */}
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search by title, author, course..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Category Filter */}
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Types</option>
                <option value="BUY">Want to Buy</option>
                <option value="SELL">Want to Sell</option>
                <option value="BORROW">Want to Borrow</option>
              </select>

              {/* Sort Filter */}
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as any)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="title">Title A-Z</option>
                <option value="price">Price High-Low</option>
              </select>
            </div>
          </div>
        </div>

        <div
          className={cn(
            "flex-1 overflow-y-auto transition-[padding] duration-300",
            isFilterExpanded && !isMobile
              ? "pt-[calc(var(--header-height)+8.5rem)]"
              : "pt-[calc(var(--header-height)+5rem)]"
          )}
        >
          <div className="w-full p-6">
            {/* Loading State */}
            {isLoading && (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="text-gray-600 mt-2">Loading textbooks...</p>
                </div>
              </div>
            )}

            {/* Error State */}
            {error && (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <BookOpen className="w-12 h-12 text-red-500 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Error Loading Textbooks
                  </h3>
                  <p className="text-gray-600">Please try again later.</p>
                </div>
              </div>
            )}

            {/* Empty State */}
            {!isLoading && !error && filteredItems.length === 0 && (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No Textbooks Found
                  </h3>
                  <p className="text-gray-600">
                    {searchTerm || selectedCategory !== "all"
                      ? "Try adjusting your search or filters."
                      : "Be the first to list a textbook!"}
                  </p>
                </div>
              </div>
            )}

            {/* Content Grid */}
            {!isLoading && !error && filteredItems.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredItems.map((item) => (
                  <div
                    key={item.id}
                    className={`${enhancedPageLayoutStyles.card.container} cursor-pointer hover:shadow-md transition-shadow`}
                    onClick={() => {
                      if (selectedLocation?.locationCode) {
                        navigate(
                          `/locations/${selectedLocation.locationCode}/textbooks/${item.id}`,
                        );
                      } else {
                        navigate(`/textbooks/${item.id}`);
                      }
                    }}
                  >
                    <div className={enhancedPageLayoutStyles.card.content}>
                      <div className={enhancedPageLayoutStyles.card.header}>
                        <div className="flex items-center gap-2">
                          <span
                            className={`px-2 py-1 text-xs font-medium rounded ${getCategoryColor(
                              item.metadata?.category,
                            )}`}
                          >
                            {item.metadata?.category}
                          </span>
                          {item.metadata?.condition && (
                            <span
                              className={`px-2 py-1 text-xs font-medium rounded ${getConditionColor(
                                item.metadata.condition,
                              )}`}
                            >
                              {item.metadata.condition?.replace("_", " ")}
                            </span>
                          )}
                        </div>
                        {item.metadata?.price && (
                          <div className="text-lg font-bold text-success">
                            ${item.metadata.price}
                          </div>
                        )}
                      </div>

                      <h3 className={enhancedPageLayoutStyles.card.title}>
                        {item.title}
                      </h3>

                      <div className="text-sm text-gray-500 mb-2">
                        {item.metadata?.author && (
                          <span>by {item.metadata.author}</span>
                        )}
                      </div>

                      {item.metadata?.course && (
                        <div className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded inline-block mb-3">
                          {item.metadata.course}
                        </div>
                      )}

                      <p className={enhancedPageLayoutStyles.card.description}>
                        {item.description}
                      </p>

                      <div className={enhancedPageLayoutStyles.card.footer}>
                        <div className={enhancedPageLayoutStyles.card.meta}>
                          <span className="text-sm text-content-text-muted">
                            Posted by {item.author.name || item.author.email}
                          </span>
                        </div>
                        <div
                          className={enhancedPageLayoutStyles.card.actions}
                          onClick={(e) => e?.stopPropagation()}
                        >
                          {user?.isAdmin && (
                            <>
                              <EnhancedIconButton
                                onClick={(e) => {
                                  e?.stopPropagation();
                                  if (selectedLocation?.locationCode) {
                                    navigate(
                                      `/locations/${selectedLocation.locationCode}/textbooks/${item.id}/edit`,
                                    );
                                  } else {
                                    navigate(`/textbooks/${item.id}/edit`);
                                  }
                                }}
                                icon={Edit}
                                title="Edit item"
                              />
                              <EnhancedIconButton
                                onClick={() => handleDelete(item.id)}
                                icon={Trash2}
                                title="Delete item"
                                variant="danger"
                              />
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
